import React from 'react';
import {
  Paper,
  Title,
  Text,
  Group,
  Button,
  Stack,
  Avatar,
  UnstyledButton,
} from '@mantine/core';
import { IconPlus, IconRoute } from '@tabler/icons-react';
import type {
  TravelerView,
  MembershipVisitRequest,
  UserSummary,
} from '../../types';
import { MembershipVisitRow } from './MembershipVisitRow';

export interface TravelerItineraryProps {
  travelers: TravelerView[];
  activeTravelerId: number | null;
  activeTraveler: TravelerView | undefined;
  currentUser: UserSummary | null | undefined;
  hasCurrentUserMembership: boolean;
  showNewVisitRow: boolean;
  busy: boolean;
  isCreatingMembership: boolean;
  isAddingVisit: boolean;
  isUpdatingVisit: boolean;
  isDeletingVisit: boolean;
  onSelectTraveler: (userId: number) => void;
  onCreateMembership: () => void;
  onShowNewVisitRow: () => void;
  onAddVisit: (body: MembershipVisitRequest) => void;
  onUpdateVisit: (visitId: number, body: MembershipVisitRequest) => void;
  onDeleteVisit: (visitId: number) => void;
}

export const TravelerItinerary: React.FC<TravelerItineraryProps> = ({
  travelers,
  activeTravelerId,
  activeTraveler,
  currentUser,
  hasCurrentUserMembership,
  showNewVisitRow,
  busy,
  isCreatingMembership,
  isAddingVisit,
  isUpdatingVisit,
  isDeletingVisit,
  onSelectTraveler,
  onCreateMembership,
  onShowNewVisitRow,
  onAddVisit,
  onUpdateVisit,
  onDeleteVisit,
}) => (
  <Paper shadow="sm" p="lg" radius="md" withBorder>
    <Title order={3} mb="md">
      Destinations
    </Title>
    <Group
      gap="sm"
      mb="md"
      wrap="wrap"
      role="tablist"
      aria-label="Select traveler"
    >
      {travelers.map(traveler => {
        const travelerId = traveler.membership.traveler.id;
        const isActive = activeTravelerId === travelerId;
        return (
          <UnstyledButton
            key={traveler.membership.id}
            role="tab"
            id={`traveler-tab-${travelerId}`}
            aria-selected={isActive}
            aria-controls={`traveler-panel-${travelerId}`}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onSelectTraveler(travelerId)}
            data-cy={`traveler-tab-${travelerId}`}
          >
            <Group gap="xs">
              <Avatar
                size="sm"
                radius="xl"
                style={{
                  backgroundColor: traveler.color,
                  border: isActive
                    ? '2px solid var(--mantine-color-white)'
                    : undefined,
                }}
              >
                {traveler.membership.traveler.name.charAt(0)}
              </Avatar>
              <Text size="sm" fw={500}>
                {traveler.membership.traveler.name}
              </Text>
            </Group>
          </UnstyledButton>
        );
      })}
    </Group>

    {!hasCurrentUserMembership && currentUser && (
      <Stack gap="sm" mb="md">
        <Text size="sm" c="dimmed">
          You have not added your itinerary yet.
        </Text>
        <Button
          leftSection={<IconRoute size={16} />}
          onClick={onCreateMembership}
          loading={isCreatingMembership}
          data-cy="add-traveler-membership"
        >
          Add my itinerary
        </Button>
      </Stack>
    )}

    {activeTraveler && !activeTraveler.isCurrentUser && (
      <Text size="sm" c="dimmed" mb="xs">
        {activeTraveler.membership.traveler.name}&apos;s visits (read-only)
      </Text>
    )}

    {activeTraveler && (
      <Stack
        gap="sm"
        role="tabpanel"
        id={`traveler-panel-${activeTraveler.membership.traveler.id}`}
        aria-labelledby={`traveler-tab-${activeTraveler.membership.traveler.id}`}
      >
        {(activeTraveler.membership.visits ?? []).map(visit => (
          <MembershipVisitRow
            key={visit.id}
            visit={visit}
            itineraryVisits={activeTraveler.membership.visits ?? []}
            color={activeTraveler.color}
            readOnly={!activeTraveler.isCurrentUser}
            onSave={body => onUpdateVisit(visit.id, body)}
            onDelete={
              activeTraveler.isCurrentUser
                ? () => onDeleteVisit(visit.id)
                : undefined
            }
            isSaving={isUpdatingVisit || isDeletingVisit}
          />
        ))}

        {activeTraveler.isCurrentUser && hasCurrentUserMembership && (
          <>
            {showNewVisitRow && (
              <MembershipVisitRow
                itineraryVisits={activeTraveler.membership.visits ?? []}
                color={activeTraveler.color}
                readOnly={false}
                onSave={onAddVisit}
                isSaving={isAddingVisit}
              />
            )}
            {!showNewVisitRow && (
              <Button
                variant="light"
                leftSection={<IconPlus size={16} />}
                onClick={onShowNewVisitRow}
                disabled={busy}
                data-cy="add-membership-visit"
              >
                Add city
              </Button>
            )}
          </>
        )}
      </Stack>
    )}
  </Paper>
);

export default TravelerItinerary;
